import { useEffect, useRef, useState } from "react";
import styles from "./VerifyOtp.module.css";
import ModalView from "@modals/modalview/ModalView";
import {
  generateOtp,
  validateOtp,
  getLoginDto,
} from "@services/authenticationService";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setAccessToken } from "@store/login/loginStore";
import { setComponentToShow } from "@store/ModalToShow";
import { environment } from "@environments/environment";

declare global {
  interface Window {
    grecaptcha: any;
  }
}

function VerifyOtp({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const dispatch = useDispatch();
  const [timeLeft, setTimeLeft] = useState<number>(60);
  const [otp, setOtp] = useState<string[]>(["", "", "", ""]);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const [error, setError] = useState("");

  const captchaRef = useRef<HTMLDivElement>(null);
  const captchaWidgetIdRef = useRef<number | null>(null);

  const phoneDataFromStorage = localStorage.getItem("phoneData");
  const phoneData = phoneDataFromStorage
    ? JSON.parse(phoneDataFromStorage)
    : "";
  const phoneNumber = localStorage.getItem("phoneNumber");
  const countryCode = localStorage.getItem("countryCode");
  const displayNumber = `${countryCode}${phoneNumber}`;
  const navigate = useNavigate();

  const SITE_KEY = environment.captchaSiteKey;

  useEffect(() => {
    if (!isOpen) return;

    const initCaptcha = () => {
      if (window.grecaptcha && window.grecaptcha.render) {
        renderCaptcha();
      } else {
        setTimeout(initCaptcha, 100);
      }
    };

    initCaptcha();

    return () => {
      if (captchaWidgetIdRef.current !== null && window.grecaptcha) {
        try {
          window.grecaptcha.reset(captchaWidgetIdRef.current);
        } catch (error) {
          console.error("Error resetting captcha:", error);
        }
      }
    };
  }, [isOpen]);

  const renderCaptcha = () => {
    if (!captchaRef.current) {
      console.log("Captcha ref not available");
      return;
    }

    if (captchaWidgetIdRef.current !== null) {
      console.log("Captcha already rendered, ID:", captchaWidgetIdRef.current);
      return;
    }

    try {
      if (window.grecaptcha && window.grecaptcha.render) {
        captchaWidgetIdRef.current = window.grecaptcha.render(
          captchaRef.current,
          {
            sitekey: SITE_KEY,
            size: "invisible",
            callback: onCaptchaResolved,
          }
        );
        console.log(
          "Captcha rendered successfully, ID:",
          captchaWidgetIdRef.current
        );
      }
    } catch (error) {
      console.error("Error rendering captcha:", error);
    }
  };

  const onCaptchaResolved = (token: string) => {
    console.log("Token received:", token);
    resendOtpWithToken(token);
  };

  const executeCaptcha = () => {
    if (!window.grecaptcha) {
      console.error("grecaptcha not loaded yet");
      return;
    }

    if (captchaWidgetIdRef.current === null) {
      console.error("reCAPTCHA widget not initialized. Widget ID is null.");
      renderCaptcha();
      setTimeout(() => {
        if (captchaWidgetIdRef.current !== null) {
          console.log("Retrying execute after render");
          window.grecaptcha.execute(captchaWidgetIdRef.current);
        } else {
          console.error("Still couldn't initialize captcha");
        }
      }, 1000);
      return;
    }

    try {
      window.grecaptcha.execute(captchaWidgetIdRef.current);
      console.log("Captcha executed successfully");
    } catch (error) {
      console.error("Error executing captcha:", error);
    }
  };

  const resendOtpWithToken = async (token: string) => {
    const phoneDataFromStorage = localStorage.getItem("phoneData");
    if (!phoneDataFromStorage) return;

    const phoneData = JSON.parse(phoneDataFromStorage);

    const payload = { ...phoneData, captcha_token: token };

    try {
      const res = await generateOtp(payload);
      if (res) {
        if (captchaWidgetIdRef.current !== null && window.grecaptcha) {
          window.grecaptcha.reset(captchaWidgetIdRef.current);
        }
        setError("");
        setTimeLeft(60);
        dispatch(setComponentToShow("VerifyOtp"));
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
      if (captchaWidgetIdRef.current !== null && window.grecaptcha) {
        window.grecaptcha.reset(captchaWidgetIdRef.current);
      }
    }
  };

  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    setError("");
    const value = e.target.value;

    if (/^[0-9]$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (index < inputsRef.current.length - 1) {
        inputsRef.current[index + 1]?.focus();
      }
    } else {
      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);
      e.target.value = "";
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !e.currentTarget.value && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleEdit = () => {
    dispatch(setComponentToShow("EditPhoneNumber"));
  };

  const handleResendOtp = async () => {
    executeCaptcha();
  };

  const handleVerify = async () => {
    const finalOtp = otp.join("");
    console.log("Entered OTP:", finalOtp);
    const payload = {
      countryCode: phoneData?.dialCode,
      phone: phoneData?.number,
      otp: finalOtp,
      register: false,
    };
    try {
      const res = await validateOtp(payload);
      if (res && res?.accessToken) {
        localStorage.setItem("accessToken", res?.accessToken);
        dispatch(setAccessToken(res?.accessToken));
        const loginDto = await getLoginDto(res?.accessToken);
        localStorage.setItem("userDto", JSON.stringify(loginDto[0]));
        console.log("login", loginDto[0]);
        onClose();

        const roles = loginDto?.[0]?.roles || [];
        const matchedRole = roles.find(
          (role: any) => role.roleId === environment.ROLE_IDS.EVALUATOR
        );
        if (matchedRole) {
          navigate("/dashboard");
        }
      }
    } catch {
      setError("Incorrect OTP");
    }
  };

  const isOtpComplete = otp.every((digit) => digit !== "");

  return (
    <>
      {isOpen && (
        <div
          ref={captchaRef}
          className="g-recaptcha"
          data-sitekey={SITE_KEY}
          data-callback="onCaptchaResolved"
          data-size="invisible"
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            zIndex: 9999,
          }}
        />
      )}

      <ModalView isOpen={isOpen} onClose={onClose}>
        <div className={styles.verifyOtpContainer}>
          <div className={styles.verifyOtpHeader}>
            <h2>Verify OTP</h2>
            <button className={styles.closeBtn} onClick={onClose}>
              &times;
            </button>
          </div>

          <div className={styles.otpInfo}>
            <p>
              Please verify the OTP sent to{" "}
              <span className={styles.phoneNumber}>{displayNumber}</span>
            </p>
            <span className={styles.editLink} onClick={handleEdit}>
              EDIT
            </span>
          </div>

          {timeLeft > 0 ? (
            <p className={styles.otpExpiry}>OTP Expires in 1 min</p>
          ) : (
            <p>OTP expired</p>
          )}

          <div className={styles.otpInputs}>
            {[0, 1, 2, 3].map((_, index) => (
              <input
                key={index}
                type="text"
                maxLength={1}
                value={otp[index]}
                ref={(el: HTMLInputElement | null) => {
                  inputsRef.current[index] = el;
                }}
                onChange={(e) => handleInputChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
              />
            ))}
          </div>
          <div className={styles.error}>{error}</div>

          <button
            className={styles.verifyOtpBtn}
            onClick={handleVerify}
            disabled={!isOtpComplete}
          >
            VERIFY OTP
          </button>

          <p
            className={styles.resendOtp}
            style={{ cursor: timeLeft === 0 ? "pointer" : "not-allowed" }}
            onClick={timeLeft > 0 ? () => {} : handleResendOtp}
          >
            {timeLeft > 0
              ? `RESEND OTP IN ${formatTime(timeLeft)}`
              : "RESEND OTP"}
          </p>
        </div>
      </ModalView>
    </>
  );
}

export default VerifyOtp;
